import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useGroupData, addGroupFinance, deleteGroupFinance } from '../../hooks/useGroups';
import Modal from '../../components/ui/Modal';
import StatCard from '../../components/ui/StatCard';
import { fmt, fmtDate } from '../../lib/constants';

export default function GroupFinances() {
  const { profile } = useAuth();
  const { group, finances, loading, refreshFinances } = useGroupData(profile?.group_id);
  const [showAdd, setShowAdd] = useState(false);
  const [form,    setForm]    = useState({ date: new Date().toISOString().split('T')[0], type: 'income', category: '', amount: '', description: '' });
  const [saving,  setSaving]  = useState(false);

  const income  = finances.filter(f => f.type === 'income').reduce((s, f) => s + Number(f.amount), 0);
  const expense = finances.filter(f => f.type === 'expense').reduce((s, f) => s + Number(f.amount), 0);
  const balance = income - expense;

  async function handleAdd(e) {
    e.preventDefault(); setSaving(true);
    const rec = await addGroupFinance({ ...form, amount: parseFloat(form.amount), group_id: profile.group_id });
    if (rec) { setShowAdd(false); setForm({ date: new Date().toISOString().split('T')[0], type: 'income', category: '', amount: '', description: '' }); refreshFinances(); }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this entry?')) return;
    await deleteGroupFinance(id);
    refreshFinances();
  }

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>Loading…</div>;

  return (
    <div>
      <div className="flex-between mb-4">
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800 }}>💰 {group?.name} Finances</h1>
          <div style={{ fontSize: 13, color: 'var(--text3)', marginTop: 2 }}>Track income, expenses and group budget</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Record Transaction</button>
      </div>

      <div className="grid-3 mb-4">
        <StatCard icon="💰" label="Total Income"  value={fmt(income)}  color="green" />
        <StatCard icon="💸" label="Total Expenses" value={fmt(expense)} color="red" />
        <StatCard icon="🏦" label="Balance"        value={fmt(balance)} color={balance >= 0 ? 'blue' : 'red'} />
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Date</th><th>Type</th><th>Category</th><th>Amount</th><th>Description</th><th>Action</th></tr>
          </thead>
          <tbody>
            {finances.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 28, color: 'var(--text3)' }}>No financial records yet. Add the first transaction.</td></tr>
            )}
            {finances.map(f => (
              <tr key={f.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtDate(f.date)}</td>
                <td><span className={`badge ${f.type === 'income' ? 'badge-green' : 'badge-red'}`}>{f.type}</span></td>
                <td style={{ fontSize: 12 }}>{f.category || '—'}</td>
                <td style={{ fontWeight: 700, color: f.type === 'income' ? 'var(--success)' : 'var(--danger)' }}>
                  {f.type === 'expense' ? '-' : ''}{fmt(f.amount)}
                </td>
                <td style={{ fontSize: 12, color: 'var(--text3)' }}>{f.description || '—'}</td>
                <td><button className="btn btn-danger btn-sm" onClick={() => handleDelete(f.id)}>×</button></td>
              </tr>
            ))}
            {finances.length > 0 && (
              <tr style={{ background: 'var(--blue-light)', fontWeight: 700 }}>
                <td colSpan={3} style={{ padding: '10px 14px' }}>BALANCE</td>
                <td style={{ color: balance >= 0 ? 'var(--success)' : 'var(--danger)', padding: '10px 14px' }}>{fmt(balance)}</td>
                <td colSpan={2}></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      {showAdd && (
        <Modal title="Record Transaction" onClose={() => setShowAdd(false)}
          footer={<><button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button><button className="btn btn-primary" form="fin-form" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button></>}>
          <form id="fin-form" onSubmit={handleAdd}>
            <div className="grid-2">
              <div className="form-group"><label className="form-label">Date *</label><input className="form-input" type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} required /></div>
              <div className="form-group">
                <label className="form-label">Type *</label>
                <select className="form-select" value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} required>
                  <option value="income">Income</option>
                  <option value="expense">Expense</option>
                </select>
              </div>
              <div className="form-group"><label className="form-label">Category</label><input className="form-input" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} placeholder="e.g. Dues, Retreat, Materials" /></div>
              <div className="form-group"><label className="form-label">Amount (KES) *</label><input className="form-input" type="number" min="1" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required /></div>
            </div>
            <div className="form-group"><label className="form-label">Description</label><input className="form-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
